import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import AuthService from '../services/auth';
import { MainLayout } from '../layouts';
import { routesPath } from '../App';
import { isSelfHost } from '../utils/selfHost';

const SELF_HOST = isSelfHost();

const ProtectedRoute = ({ children, hideSidebar = false }) => {
  const [user, setUser] = useState(null);
  const location = useLocation();
  const isAuthenticated = AuthService.isAuthenticated();
  
  // Check for test mode bypass
  const searchParams = new URLSearchParams(location.search);
  const skipAuth = searchParams.get('skipAuth') === 'true' || 
                   process.env.REACT_APP_SKIP_AUTH === 'true' ||
                   window.localStorage.getItem('skipAuth') === 'true';
  
  useEffect(() => {
    if (isAuthenticated) {
      const currentUser = AuthService.getCurrentUser();
      setUser(currentUser);
    } else if (skipAuth) {
      // Create a mock user for testing
      setUser({
        email: 'test@example.com',
        name: 'Test User',
        userId: 'test-user-id'
      });
    }
  }, [isAuthenticated, skipAuth]);
  
  // Redirect authenticated users to studio subdomain
  useEffect(() => {
    // Self-host is single-origin (the control-plane) — no studio.* subdomain
    // to bounce to. Skip the SaaS redirect entirely.
    if (SELF_HOST) return;
    if (isAuthenticated && typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      if (hostname !== 'localhost' && hostname !== '127.0.0.1' && !hostname.startsWith('studio')) {
        const rootHostname = hostname.replace(/^www\./, '');
        const studioHostname = 'studio.' + rootHostname;
        const studioUrl = `${window.location.protocol}//${studioHostname}${window.location.pathname}${window.location.search}${window.location.hash}`;
        window.location.replace(studioUrl);
      }
    }
  }, [isAuthenticated]);

  // Bypass authentication for testing
  if (skipAuth) {
    console.log('⚠️ Authentication bypassed for testing');
    return (
      <MainLayout user={user} hideSidebar={hideSidebar}>
        {children}
      </MainLayout>
    );
  }
  
  if (!isAuthenticated) {
    // Self-host: no /connect SaaS login flow and no studio.* subdomain. When a
    // PAT is present the branch above already rendered; if it isn't, render the
    // children anyway (the control-plane's own auto-login / PAT-paste handles
    // it) rather than bouncing to a non-existent SaaS login.
    if (SELF_HOST) {
      return (
        <MainLayout user={user} hideSidebar={hideSidebar}>
          {children}
        </MainLayout>
      );
    }
    // If on studio subdomain, redirect to root domain
    const hostname = window.location.hostname;
    if (hostname.startsWith('studio.') || hostname.startsWith('studio-')) {
      const rootHostname = hostname.replace('studio.', '').replace('studio-', '');
      const rootUrl = `${window.location.protocol}//${rootHostname}${routesPath.connect}`;
      window.location.replace(rootUrl);
      return null;
    }
    return <Navigate to={routesPath.connect} replace />;
  }
  
  return (
    <MainLayout user={user} hideSidebar={hideSidebar}>
      {children}
    </MainLayout>
  );
};

export default ProtectedRoute;
